import { createFileRoute, Link } from "@tanstack/react-router";
import { Plus, Check } from "lucide-react";
import { useState, useMemo, useEffect } from "react";
import { useInquiry } from "@/lib/inquiry-store";
import { SiteLayout } from "@/components/site/SiteLayout";
import { snacksSeed } from "@/lib/snacks-data";
import { seo } from "@/lib/seo";

export const Route = createFileRoute("/snacks")({
  head: () => ({
    meta: [
      ...seo({
        title: "Snacks & Confections – Wholesale QT Foods",
        description: "Authentic snacks and confections from Japan to pair with our premium teas.",
      }),
    ],
  }),
  component: SnacksPage,
});

function SnackCard({ product }: { product: (typeof snacksSeed)[0] }) {
  const { add } = useInquiry();
  const [added, setAdded] = useState(false);

  const onAdd = (e: React.MouseEvent) => {
    e.preventDefault();
    add({
      productHandle: product.handle,
      productName: product.name,
      categoryName: product.categories.name,
      imageUrl: product.image_url,
    });
    setAdded(true);
    setTimeout(() => setAdded(false), 1500);
  };

  return (
    <div className="group flex flex-col">
      <Link
        to="/snacks/$handle"
        params={{ handle: product.handle }}
        className="block overflow-hidden bg-muted aspect-square flex items-center justify-center"
      >
        <img
          src={product.image_url}
          alt={product.name}
          loading="lazy"
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
      </Link>
      <div className="mt-4 flex flex-1 flex-col">
        <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground">
          {product.categories.name}
        </p>
        <Link
          to="/snacks/$handle"
          params={{ handle: product.handle }}
          className="mt-1 font-serif text-lg leading-snug hover:text-primary"
        >
          {product.name}
        </Link>
        <button
          onClick={onAdd}
          className="mt-4 inline-flex items-center justify-center gap-2 self-start rounded-sm border border-border px-4 py-2 text-xs font-medium uppercase tracking-[0.15em] transition-colors hover:border-primary hover:text-primary"
        >
          {added ? (
            <>
              <Check className="h-3.5 w-3.5" /> Added
            </>
          ) : (
            <>
              <Plus className="h-3.5 w-3.5" /> Add to Inquiry
            </>
          )}
        </button>
      </div>
    </div>
  );
}

function SnacksPage() {
  const products = snacksSeed;

  // Group products by category
  const groupedProducts = useMemo(() => {
    const groups: Record<string, any[]> = {};
    products.forEach((p: any) => {
      const cat = p.categories?.name || "Other";
      if (!groups[cat]) groups[cat] = [];
      groups[cat].push(p);
    });
    return groups;
  }, [products]);

  const sortedCategories = Object.keys(groupedProducts).sort();

  // Scroll Spy logic
  const [activeSection, setActiveSection] = useState<string>("");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        let currentActives: string[] = [];
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            currentActives.push(entry.target.id);
          }
        });
        if (currentActives.length > 0) {
          setActiveSection(currentActives[0]);
        }
      },
      { rootMargin: "-20% 0px -80% 0px" },
    );

    sortedCategories.forEach((cat) => {
      const el = document.getElementById(`section-${cat}`);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [sortedCategories, products]);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      const y = el.getBoundingClientRect().top + window.scrollY - 100;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  };

  return (
    <SiteLayout breadcrumbs={[{ label: "Snacks & More" }]}>
      <section className="mx-auto max-w-7xl px-5 pt-10 pb-20 lg:px-8 lg:pt-14 lg:pb-28">
        <h1 className="mt-3 font-serif text-5xl md:text-6xl leading-[1.05]">
          Japanese Snacks & More
        </h1>
        <div className="mt-6 max-w-2xl text-base leading-relaxed text-foreground/85">
          <p>
            Discover our curated selection of authentic Japanese snacks, confections, noodles, and
            condiments, perfectly crafted to complement our premium teas. Available for wholesale
            and private label.
          </p>
        </div>

        <div className="mt-16 flex flex-col lg:flex-row gap-12 items-start relative">
          {/* Main Content */}
          <div className="flex-1 w-full order-2 lg:order-1">
            <div className="mb-8 text-sm text-muted-foreground flex items-center justify-between">
              <span>
                Showing {products.length} snack{products.length !== 1 && "s"}
              </span>
            </div>

            {products.length === 0 ? (
              <p className="text-muted-foreground py-10">No snacks available at the moment.</p>
            ) : (
              <div className="flex flex-col gap-16">
                {sortedCategories.map((cat) => (
                  <div key={cat} id={`section-${cat}`} className="scroll-mt-24">
                    <h2 className="font-serif text-3xl mb-6 pb-2 border-b border-border">{cat}</h2>
                    <div className="grid grid-cols-2 gap-6 md:grid-cols-3">
                      {groupedProducts[cat].map((p: any) => (
                        <SnackCard key={p.id} product={p} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Sticky Sidebar */}
          {sortedCategories.length > 0 && (
            <aside className="hidden lg:block w-64 shrink-0 sticky top-24 order-1 lg:order-2 self-start border-l border-border pl-6">
              <h3 className="text-sm font-semibold uppercase tracking-wider mb-4 text-foreground/80">
                Categories
              </h3>
              <ul className="flex flex-col gap-2.5">
                {sortedCategories.map((cat) => (
                  <li key={cat}>
                    <button
                      onClick={() => scrollToSection(`section-${cat}`)}
                      className={`text-sm text-left w-full transition-colors ${
                        activeSection === `section-${cat}`
                          ? "text-primary font-medium"
                          : "text-muted-foreground hover:text-foreground"
                      }`}
                    >
                      {cat}
                    </button>
                  </li>
                ))}
              </ul>
            </aside>
          )}
        </div>
      </section>
    </SiteLayout>
  );
}
